import classnames from 'classnames/bind'
import React, { useCallback, useRef } from 'react'
import { useTranslate } from 'react-polyglot'

import Accordion from '~/components/Accordion'
import AccordionPanel from '~/components/Accordion/AccordionPanel'
import InlineCta from '~/components/InlineCta'
import LangSwitcher from '~/components/LangSwitcher'
import Link, { LinkProps } from '~/components/Link'
import { NavigationProps } from '~/components/Navigation'
import RichText from '~/components/RichText'
import RoundedCta from '~/components/RoundedCta'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { useStyle } from '~/providers/StyleProvider'

import useClickOutside from '~/hooks/useClickOutside'

import { isCurrentRoute } from '~/utils/is-current-route'

import { NavigationItemProps } from '../NavigationDesktop'
import { ReactComponent as DropdownIcon } from '../dropdown.svg'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface DropdownProps extends NavigationProps {
  className?: string
  isOpen?: boolean
  onClose?: () => void
}

export interface DropDownAccordionProps {
  className?: string
  item?: NavigationItemProps
  hasBorder?: boolean
}

function DropDownAccordion({
  className,
  item,
  hasBorder,
}: DropDownAccordionProps) {
  const ctaStyle = useStyle({ textPreset: 'cta-label-small' })
  return (
    <Accordion>
      <AccordionPanel
        className={css.accordionItem}
        head={(isActive: boolean) => {
          return (
            <div
              className={cx(
                css.linkHeader,
                css.accordionHead,
                ctaStyle,
                className,
                {
                  isActive,
                  hasBorder,
                },
              )}>
              {item?.title ? (
                Array.isArray(item.title) ? (
                  <RichText render={item?.title} />
                ) : item?.title ? (
                  item.title
                ) : null
              ) : null}
              <DropdownIcon className={css.icon} width={10} height={10} />
            </div>
          )
        }}
        body={
          <div className={css.sectionBody}>
            {item?.navigationItem?.map((link, index) => (
              <div className={cx(css.linkHeader)} key={`dropdown_${index}`}>
                <Link {...link} className={cx(css.link)} />
                <DropdownIcon className={css.icon} width={10} height={10} />
              </div>
            ))}
          </div>
        }
      />
    </Accordion>
  )
}

function Dropdown({
  className,
  isOpen,
  items,
  alternateLangs,
  login,
  register,
  onClose,
}: DropdownProps) {
  const t = useTranslate()
  const { redirects, global } = useGlobalData()

  let animateIndex = 0
  function animated() {
    const s = cx(css.animatedItem, css?.[`animatedItem-${animateIndex}`])
    animateIndex++
    return s
  }

  return (
    <nav className={cx(css.Dropdown, className, { isOpen })}>
      <div className={css.content}>
        {items?.map((item, index) => {
          if (!item) return null
          const currentRoute =
            'href' in item ? isCurrentRoute(item?.href) : false
          return 'navigationItem' in item ? (
            <DropDownAccordion
              item={item}
              className={animated()}
              key={`accordion_${index}`}
            />
          ) : (
            <div className={cx(css.linkHeader, animated())} key={index}>
              <InlineCta
                {...item}
                className={cx(css.link)}
                type="left_center"
                textPreset="cta-label-small"
                theme={currentRoute ? 'primary' : 'inherit'}
                lineProps={{
                  initialLineState: currentRoute ? 'underlined' : 'none',
                  thickness: 2,
                }}
              />
              <DropdownIcon className={css.icon} width={10} height={10} />
            </div>
          )
        })}
        {alternateLangs && global?.asLangSwitcher && (
          <div
            className={cx(css.linkHeader, animated(), {
              hasBorder: items?.length > 0,
            })}>
            <LangSwitcher
              className={css.langSwitcher}
              textPreset="cta-label-small"
              alternateLangs={alternateLangs}
            />
          </div>
        )}
        {login && 'navigationItem' in login ? (
          <DropDownAccordion
            hasBorder={true}
            item={login}
            className={cx(animated(), css.loginAccordionHead)}
          />
        ) : (
          login && (
            <div
              className={cx(css.linkHeader, css.login, animated(), {
                hasBorder: true,
              })}>
              <InlineCta {...login} className={cx(css.link)} />
              <DropdownIcon className={css.icon} width={10} height={10} />
            </div>
          )
        )}

        {register && (
          <RoundedCta
            className={cx(css.register, animated())}
            theme="primary"
            size="normal"
            {...register}
          />
        )}
      </div>
    </nav>
  )
}

Dropdown.defaultProps = {}

export default Dropdown
